import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuItem } from '@mui/material';

import { Divider, IconButton, TableCell, TableRow, MoreVertIcon } from '@occc-app/design-system';
import { RetailerNetworkPartialFragment } from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetwork } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { routeCreator } from '../../retailer-connect.utils';

type RetailerNetworkTableListRowProps = {
  item: RetailerNetworkPartialFragment;
  onClick?: (uuid: string) => void;
};

export const RetailerNetworkListTableRow = ({
  item,
  onClick,
}: RetailerNetworkTableListRowProps) => {
  const navigate = useNavigate();
  const [actionsForUuid, setActionsForUuid] = useState<string | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { uuid, name, adminName, customerId, numberOfLocations } = item;

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const getHandlerForAdditionalActions =
    (uuid: string) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(uuid);
    };

  const getMenuItemAction =
    (routeName: 'details' | 'edit') => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      const routes = {
        details: routeCreator.myStores(uuid),
        edit: routeCreator.configurationUpdateNetworkDetails(uuid),
      };

      navigate(routes[routeName]);
      handleClose();
    };

  const click = useCallback(() => {
    onClick?.(uuid);
  }, [onClick, uuid]);

  return (
    <TableRow key={uuid} onClick={click}>
      <TableCell>{name}</TableCell>
      <TableCell>{customerId}</TableCell>
      <TableCell>{adminName}</TableCell>
      <TableCell>{numberOfLocations}</TableCell>
      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(uuid)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={uuid === actionsForUuid}
          onClose={(event) => {
            (event as any).stopPropagation();
            handleClose();
          }}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={getMenuItemAction('details')}>View Retailer Network Details</MenuItem>

          <Can I={UpdateRetailerNetwork({ uuid: item.uuid })}>
            <Divider key='divider' />
            <MenuItem key='edit' onClick={getMenuItemAction('edit')}>
              Edit Retailer Network Details
            </MenuItem>
          </Can>
        </Menu>
      </TableCell>
    </TableRow>
  );
};
